import {
  TCheckOrganizationResponse,
  TOrganizationAverageScoreResponse,
  TPersonalAndOrgScoreResponse,
  TPublicScreenerFormForBE,
  TSendResultsToEmailForBE,
  TValidateOrganizationResponse,
} from '@/types/PublicScreener.types';
import { api, TCustomAxiosResponse } from '..';
import { apiPaths } from '@/services/api/paths';
import { WELLNESS_DATA_MULTIPLIER } from '@/constants';
import { THealthDomainScores } from '@/types/Health.types';

export const validateOrgGet = async (slug: string): Promise<TValidateOrganizationResponse> => {
  const { data } = await api.get(apiPaths.publicScreener.validateOrg(slug));
  return data.data;
};

export const orgScoreDataGet = async (
  slug: string,
): Promise<TOrganizationAverageScoreResponse> => {
  const { data } = await api.get<{ data: TOrganizationAverageScoreResponse }>(apiPaths.publicScreener.orgScore(slug));

  const wellnessDomainsMultiplied = data.data.domains;

  for (const key in wellnessDomainsMultiplied) {
    wellnessDomainsMultiplied[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
  }

  return {
    totalSubmissions: data.data.totalSubmissions,
    score: data.data?.score ? data.data?.score * WELLNESS_DATA_MULTIPLIER : 0,
    domains: wellnessDomainsMultiplied,
  };
};

export const checkOrgPost = async (url: string, turnstileToken: string): Promise<TCheckOrganizationResponse> => {
  const { data } = await api.post<TCustomAxiosResponse<TCheckOrganizationResponse>>(
    apiPaths.publicScreener.checkOrganization,
    { url },
    {
      headers: { 'cf-turnstile-response': turnstileToken },
    },
  );

  const response = data.data;

  return response;
};

export const createPublicScreenerResultPost = async ({
  orgSlug,
  data,
  turnstileToken,
}: {
  orgSlug: string;
  data: TPublicScreenerFormForBE;
  turnstileToken: string;
}): Promise<{ data: { submissionId: string } }> => {
  const response = await api.post<TCustomAxiosResponse<{ submissionId: string }>>(
    apiPaths.publicScreener.orgSubmit(orgSlug),
    {
      ...data,
    },
    {
      headers: { 'cf-turnstile-response': turnstileToken },
    },
  );

  const responseData = response.data;

  return responseData;
};

export const sendResultsToEmail = async ({ formData, turnstileToken }: {formData: TSendResultsToEmailForBE, turnstileToken: string | null}): Promise<TCheckOrganizationResponse> => {
  const response = await api.post<TCustomAxiosResponse<TCheckOrganizationResponse>>(apiPaths.publicScreener.sendResultsEmail, {
    ...formData,
  }, {
    headers: { 'cf-turnstile-response': turnstileToken },
  });

  return response.data.data;
};

export const screenerResultsByTokenGet = async ({token }:{token: string}): Promise<TPersonalAndOrgScoreResponse> => {
  const { data } = await api.get(apiPaths.publicScreener.resultsByToken(token));

  const personalWellnessDomainsMultiplied = data.data.personal.domains;

  for (const key in personalWellnessDomainsMultiplied) {
    personalWellnessDomainsMultiplied[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
  }

  const organizationWellnessDomainsMultiplied = data.data.organization.domains;

  for (const key in organizationWellnessDomainsMultiplied) {
    organizationWellnessDomainsMultiplied[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
  }

  return {
    ...data.data,
    organization: {
      ...data.data.organization,
      domains: organizationWellnessDomainsMultiplied,
      average: data.data.organization.average * WELLNESS_DATA_MULTIPLIER,
    },
    personal: {
      ...data.data.personal,
      domains: personalWellnessDomainsMultiplied,
      average: data.data.personal.average * WELLNESS_DATA_MULTIPLIER,
    },
  };
};
